"use client";

import React from "react";
import { getFileNameFromPath } from "../../utils";

type ImageMeta = { url: string; width?: number; height?: number; name?: string };

export function useAssetMeta(
  isImageBlock: boolean,
  imageData: any,
  onChange: (updater: (prev: any) => any) => void,
) {
  const [assetMeta, setAssetMeta] = React.useState<ImageMeta | null>(null);

  React.useEffect(() => {
    if (!isImageBlock) return;
    if (!imageData?.assetId) {
      if (imageData?.previewUrl || imageData?.url) {
        setAssetMeta({
          url: imageData.previewUrl ?? imageData.url,
          width: imageData.width,
          height: imageData.height,
          name: imageData.sourceName ?? getFileNameFromPath(imageData.previewUrl ?? imageData.url),
        });
      } else {
        setAssetMeta(null);
      }
      return;
    }

    if (imageData.previewUrl) {
      setAssetMeta({
        url: imageData.previewUrl,
        width: imageData.width,
        height: imageData.height,
        name: imageData.sourceName ?? getFileNameFromPath(imageData.previewUrl ?? undefined),
      });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/cms/assets/${imageData.assetId}`);
        if (!res.ok) return;
        const json = await res.json();
        if (cancelled) return;
        const asset = json?.asset;
        if (asset?.originalPath) {
          setAssetMeta({
            url: asset.originalPath,
            width: asset.width ?? undefined,
            height: asset.height ?? undefined,
            name: getFileNameFromPath(asset.originalPath),
          });
        }
      } catch {
        if (!cancelled) setAssetMeta(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isImageBlock, imageData?.assetId, imageData?.previewUrl, imageData?.url, imageData?.width, imageData?.height, imageData?.sourceName]);

  React.useEffect(() => {
    if (!isImageBlock || !assetMeta) return;
    const needsDimensions =
      (assetMeta.width && assetMeta.height && (imageData?.width !== assetMeta.width || imageData?.height !== assetMeta.height)) ||
      (!imageData?.sourceName && assetMeta.name);
    if (!needsDimensions) return;
    onChange((prev) => ({
      ...prev,
      width: assetMeta.width ?? prev.width,
      height: assetMeta.height ?? prev.height,
      sourceName: prev.sourceName ?? assetMeta.name,
      previewUrl: prev.previewUrl ?? assetMeta.url,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetMeta?.width, assetMeta?.height, assetMeta?.name, assetMeta?.url, isImageBlock, imageData?.height, imageData?.width, imageData?.sourceName, onChange]);

  const clearImage = React.useCallback(() => {
    onChange((prev) => ({
      ...prev,
      assetId: undefined,
      url: undefined,
      width: undefined,
      height: undefined,
      previewUrl: undefined,
      sourceName: undefined,
      alt: "",
    }));
  }, [onChange]);

  return { assetMeta, clearImage };
}

export function useCopyFeedback(onCopy: () => Promise<boolean>) {
  const [copied, setCopied] = React.useState(false);
  const copyTimeoutRef = React.useRef<number | null>(null);

  React.useEffect(
    () => () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    },
    [],
  );

  const handleCopy = React.useCallback(async () => {
    const success = await onCopy();
    if (!success) return;
    setCopied(true);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = window.setTimeout(() => setCopied(false), 2000);
  }, [onCopy]);

  return { copied, handleCopy };
}

export function useInert(ref: React.RefObject<HTMLElement | null>, enabled: boolean) {
  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (enabled) node.setAttribute("inert", "");
    else node.removeAttribute("inert");
    return () => node.removeAttribute("inert");
  }, [enabled, ref]);
}
